<template>
  <div class="pipeline-steps">
    <!-- Display for Minimum Price Strategy -->
    <template v-if="minimumPrice > 0">
      <PipelineStepItem
        icon="filter"
        iconBgClass="bg-warning-subtle"
        iconTextClass="text-warning"
        :title="
          $t('tradingCenter.overview.spot.visualization.minimumPrice.title', {
            price: minimumPrice,
          })
        "
        :description="
          $t(
            'tradingCenter.overview.spot.visualization.minimumPrice.description',
            { price: minimumPrice }
          )
        "
        badgeIcon="clock"
        badgeTextClass="text-warning"
        :badgeText="
          $t(
            'tradingCenter.overview.spot.visualization.minimumPrice.firstMonths',
            { duration: durationMonths }
          )
        "
      />

      <!-- Step 3: Highest Bid Pool -->
      <PipelineStepItem
        icon="gavel"
        iconBgClass="bg-primary-subtle"
        iconTextClass="text-primary"
        :title="
          $t('tradingCenter.overview.spot.visualization.highestBid.title')
        "
        :description="
          $t('tradingCenter.overview.spot.visualization.highestBid.description')
        "
        badgeIcon="clock"
        badgeTextClass="text-primary"
        :badgeText="
          $t(
            'tradingCenter.overview.spot.visualization.highestBid.lastMonths',
            { duration: remainingMonths }
          )
        "
      />

      <DurationWarnings :durationMonths="durationMonths" spacingClass="mt-4" />

      <div class="mt-4">
        <MinimumPriceInfoCard />
      </div>
    </template>

    <!-- Display for Automatic Highest Bid Strategy -->
    <template v-else>
      <PipelineStepItem
        icon="gavel"
        iconBgClass="bg-primary-subtle"
        iconTextClass="text-primary"
        :title="
          $t('tradingCenter.overview.spot.visualization.highestBid.title')
        "
        :description="
          $t('tradingCenter.overview.spot.visualization.highestBid.description')
        "
      />
    </template>
  </div>
</template>
<script setup lang="ts">
import PipelineStepItem from "@/components/common/PipelineStepItem.vue";
import { getSpotMinimumPrice } from "@/composables/useSpotMinimumPrice";
import DurationWarnings from "@/dashboard/common/DurationWarnings.vue";
import MinimumPriceInfoCard from "@/dashboard/production_strategy/overview/spot/MinimumPriceInfoCard.vue";
import { useProductionStrategyStore } from "@/dashboard/production_strategy/useProductionStrategyStore";
import type { Company } from "@/types";
import { computed } from "vue";

export interface SpotSalesStrategyOverviewProps {
  company: Company;
}

const props = defineProps<SpotSalesStrategyOverviewProps>();
const productionStore = useProductionStrategyStore();

// Derive min price from active SPOT_MIN_PRICE portfolio using typed logic
const minimumPrice = computed(() => {
  return getSpotMinimumPrice(productionStore.portfolios);
});

// Get duration from the company's SPOT_MIN_PRICE portfolio
const durationMonths = computed(() => {
  return props.company.id
    ? productionStore.getDurationMonthsForCompany(props.company.id)
    : 6;
});

// Calculate remaining months until 12-month deadline
const remainingMonths = computed(() => {
  return 12 - durationMonths.value;
});
</script>
